import React from 'react';
import { generateTransactionId } from '../../utils/crypto';

interface PaymentProcessorProps {
  method: string;
  amount: number;
  onSuccess: () => void;
  onError: (error: string) => void;
}

const PaymentProcessor: React.FC<PaymentProcessorProps> = ({
  method,
  amount,
  onSuccess,
  onError
}) => {
  const processPiPayment = async () => {
    try {
      if ((window as any).Pi) {
        console.log('Processing Pi payment...', { amount });
        
        const paymentData = {
          amount: amount,
          memo: `Premium Subscription - π ${amount}`,
          metadata: { 
            productId: 'premium_subscription',
            userId: 'current_user_id',
            timestamp: new Date().toISOString(),
            paymentMethod: 'pi_network'
          }
        };
        
        const payment = (window as any).Pi.createPayment(paymentData, {
          onReadyForServerApproval: (paymentId: string) => {
            console.log('Pi Payment ready for approval:', paymentId);
            saveTransaction({
              id: paymentId,
              type: 'buy',
              amount: amount,
              currency: 'π',
              paymentMethod: 'Pi Network',
              paymentMethodIcon: '🥧',
              description: 'Premium Subscription',
              status: 'pending',
              date: new Date().toISOString(),
              feeOption: 'sender_pays',
              fee: 0
            });
          },
          onReadyForServerCompletion: (paymentId: string, txid: string) => {
            console.log('Pi Payment completed:', paymentId, txid);
            updateTransactionStatus(paymentId, 'completed');
            onSuccess();
          },
          onCancel: (paymentId: string) => {
            console.log('Pi Payment cancelled:', paymentId);
            updateTransactionStatus(paymentId, 'cancelled');
            onError('Payment was cancelled');
          },
          onError: (error: any, payment: any) => {
            console.error('Pi Payment error:', error);
            if (payment?.identifier) {
              updateTransactionStatus(payment.identifier, 'failed');
            }
            onError('Pi payment failed: ' + error.message);
          }
        });
        
        return payment;
      } else {
        throw new Error('Pi SDK not available. Please ensure you have the Pi Browser or Pi Network app installed.');
      }
    } catch (error) {
      console.error('Pi payment failed:', error);
      onError('Pi payment failed: ' + (error as Error).message);
    }
  };

  const processStripePayment = async () => {
    // Stripe payment is handled by the StripeCheckout component
    onSuccess();
  };

  const processPayPalPayment = async () => {
    try {
      const transactionId = generateTransactionId();
      
      // Open PayPal payment window
      const paypalUrl = `https://www.paypal.com/checkoutnow?token=EC-${transactionId}`;
      const paypalWindow = window.open(
        paypalUrl, 
        'PayPal', 
        'width=800,height=600,scrollbars=yes,resizable=yes'
      );
      
      saveTransaction({
        id: transactionId,
        type: 'buy',
        amount: amount,
        currency: 'USD',
        paymentMethod: 'PayPal',
        paymentMethodIcon: '💙',
        description: 'Premium Subscription',
        status: 'pending',
        date: new Date().toISOString(),
        feeOption: 'sender_pays',
        fee: amount * 0.029 + 0.30
      });

      // Simulate PayPal payment completion
      setTimeout(() => {
        if (paypalWindow) {
          paypalWindow.close();
        }
        updateTransactionStatus(transactionId, 'completed');
        onSuccess();
      }, 3000);
      
    } catch (error) {
      onError('PayPal payment failed: ' + (error as Error).message);
    }
  };

  const processGooglePayPayment = async () => {
    try {
      const transactionId = generateTransactionId();
      
      saveTransaction({
        id: transactionId,
        type: 'buy',
        amount: amount,
        currency: 'USD',
        paymentMethod: 'Google Pay',
        paymentMethodIcon: '🟢',
        description: 'Premium Subscription',
        status: 'pending',
        date: new Date().toISOString(),
        feeOption: 'sender_pays',
        fee: amount * 0.025
      });

      // Simulate Google Pay processing
      await new Promise(resolve => setTimeout(resolve, 2000));
      updateTransactionStatus(transactionId, 'completed');
      onSuccess();
    } catch (error) {
      onError('Google Pay payment failed: ' + (error as Error).message);
    }
  };

  const processApplePayPayment = async () => {
    try {
      const transactionId = generateTransactionId();
      
      saveTransaction({
        id: transactionId,
        type: 'buy',
        amount: amount,
        currency: 'USD',
        paymentMethod: 'Apple Pay',
        paymentMethodIcon: '🍎',
        description: 'Premium Subscription',
        status: 'pending',
        date: new Date().toISOString(),
        feeOption: 'sender_pays',
        fee: amount * 0.025
      });

      // Simulate Apple Pay processing
      await new Promise(resolve => setTimeout(resolve, 2000));
      updateTransactionStatus(transactionId, 'completed');
      onSuccess();
    } catch (error) {
      onError('Apple Pay payment failed: ' + (error as Error).message);
    }
  };

  const processSamsungPayPayment = async () => {
    try {
      const transactionId = generateTransactionId();
      
      saveTransaction({
        id: transactionId,
        type: 'buy',
        amount: amount,
        currency: 'USD',
        paymentMethod: 'Samsung Pay',
        paymentMethodIcon: '📱',
        description: 'Premium Subscription',
        status: 'pending',
        date: new Date().toISOString(),
        feeOption: 'sender_pays',
        fee: amount * 0.025
      });

      // Simulate Samsung Pay processing
      await new Promise(resolve => setTimeout(resolve, 2000));
      updateTransactionStatus(transactionId, 'completed');
      onSuccess();
    } catch (error) {
      onError('Samsung Pay payment failed: ' + (error as Error).message);
    }
  };

  const saveTransaction = (transaction: any) => {
    const existingTransactions = JSON.parse(localStorage.getItem('piDatingTransactions') || '[]');
    existingTransactions.unshift(transaction);
    localStorage.setItem('piDatingTransactions', JSON.stringify(existingTransactions));
  };

  const updateTransactionStatus = (transactionId: string, status: string) => {
    const existingTransactions = JSON.parse(localStorage.getItem('piDatingTransactions') || '[]');
    const updatedTransactions = existingTransactions.map((t: any) => 
      t.id === transactionId ? { ...t, status } : t
    );
    localStorage.setItem('piDatingTransactions', JSON.stringify(updatedTransactions));
  };

  const processPayment = async () => {
    switch (method) {
      case 'pi':
        return processPiPayment();
      case 'stripe':
        return processStripePayment();
      case 'paypal':
        return processPayPalPayment();
      case 'googlepay':
        return processGooglePayPayment();
      case 'applepay':
        return processApplePayPayment();
      case 'samsungpay':
        return processSamsungPayPayment();
      default:
        onError('Unsupported payment method');
    }
  };

  return { processPayment };
};

export default PaymentProcessor;